import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Shield, Eye, EyeOff } from 'lucide-react';

export default function WelcomePage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      await api.post('/auth/set-password', { password });
      await api.post('/auth/complete-onboarding');
      if (refreshUser) await refreshUser();
      navigate(user?.role === 'CONTRACTOR' ? '/my/dashboard' : '/admin/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
    setSaving(false);
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await api.post('/auth/complete-onboarding');
      if (refreshUser) await refreshUser();
      navigate(user?.role === 'CONTRACTOR' ? '/my/dashboard' : '/admin/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to continue');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-sm p-8">
          {/* Logo */}
          <div className="text-center mb-6">
            <img src="/logo.png" alt="HUBtractor" className="h-16 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">Welcome, {user?.name?.split(' ')[0]}!</h1>
          </div>

          {/* Security recommendation */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Security Recommendation</p>
                <p className="text-sm text-amber-700 mt-1">
                  You're using a temporary password. For your security, we recommend setting a personal password.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSetPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="input pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                className="input"
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="pt-4 space-y-3">
              <button
                type="submit"
                disabled={saving || !password}
                className="btn-primary w-full"
              >
                {saving ? 'Saving...' : 'Set New Password'}
              </button>

              <button
                type="button"
                onClick={handleSkip}
                disabled={saving}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Maybe later
              </button>
            </div>
          </form>

          <p className="text-xs text-center text-gray-400 mt-6">
            You can change your password anytime in Profile settings.
          </p>
        </div>
      </div>
    </div>
  );
}
